import { useAppSelector, useAppDispatch } from "app/hooks";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

import { useIntl } from "react-intl";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import { setPeers } from "store/slice/advancedPeersSearch";

// Types
import { Theme } from "@mui/material/styles";
import { ChipEntity } from "types";

import { flattenSectorProductsDomain, Peer } from "services/company";

// Store
import { setFiltersAndPeers, getAdvancedSearchOpenPreviously } from "store/slice/advancedPeersSearch";
import {
    setSelectedSectors,
    setSelectedProducts,
    setSelectedCountry,
    setPeers as setPeersStore,
} from "store/slice/Domain";
import { urlPaths } from "enums/urlPaths";

// Constants
import { PEERS_LIMIT } from "constants/constants";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        button: {
            fontSize: theme.typography.pxToRem(12),
            fontWeight: 400,
            padding: 0,
            color: theme.palette.secondary.main,
            letterSpacing: 1.25,
            minWidth: "auto",
            "&.Mui-disabled": {
                opacity: 0.5,
            },
            "&.MuiButton-textPrimary:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
            },
            "& .MuiSvgIcon-root": {
                fontSize: theme.typography.pxToRem(11),
                marginLeft: theme.spacing(0.5),
            },
            "& path": {
                fill: theme.palette.button.active.from,
            },
        },
        container: {
            marginLeft: theme.spacing(1),
        },
    })
);

const OpenAdvancedSearch = ({
    sectors,
    products,
    peers,
    company,
    countryId,
    isDisabled,
}: {
    sectors: ChipEntity[];
    products: ChipEntity[];
    peers: Peer[];
    company: ReturnType<typeof flattenSectorProductsDomain> | undefined;
    countryId: number | null;
    isDisabled?: boolean;
}) => {
    const classes = useStyles();
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const advancedSearchOpenPreviously = useAppSelector(getAdvancedSearchOpenPreviously);
    const peersSelected = peers.slice(0, PEERS_LIMIT);

    const { companyId } = useParams();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");

    const openAdvancedSearchHandler = () => {
        if (companyId) {
            if (!advancedSearchOpenPreviously) {
                const companyData = {
                    companyId: Number(companyId),
                    companyName: company?.name ?? "",
                };
                dispatch(
                    setFiltersAndPeers({
                        filters: {
                            sectors,
                            products,
                            company: companyData,
                        },
                        peers: peersSelected,
                        company: companyData,
                    })
                );
            } else {
                dispatch(setPeers(peersSelected));
            }
            dispatch(setSelectedSectors(sectors));
            dispatch(setSelectedProducts(products));
            if (countryId) {
                dispatch(setSelectedCountry(countryId));
            }

            dispatch(setPeersStore(peers));

            navigate(`${urlPaths.AdvancedPeersSearch}/${companyId}?projectId=${projectId}&insightId=${insightId}`);
        }
    };

    return (
        <Box className={classes.container}>
            <Button
                className={classes.button}
                color="primary"
                size="small"
                disabled={isDisabled}
                onClick={openAdvancedSearchHandler}
            >
                <span>{intl.formatMessage({ id: "advancedSearch.title" })}</span>
                <CustomIcon icon={icons.circledChevronRight} fontSize="small" />
            </Button>
        </Box>
    );
};

export default OpenAdvancedSearch;
